/**
 * Turn a `CinematicPath` into a pose at time t.
 *
 * This deliberately reuses the event planner's motion primitives rather than
 * growing a second motion engine:
 *  - `smoothPath` (Catmull-Rom densifier) rounds the corners,
 *  - `samplePolyline` gives arc-length-parameterized position AND the tangent
 *    bearing, which IS autorotation,
 *  - `EASES` supplies the easing curves,
 *  - `compassToSceneRotation` converts at the boundary.
 *
 * Heading conventions are the classic footgun here: the motion math works in
 * COMPASS degrees (0 = north) while the renderer wants SCENE Y-rotation
 * (0 = +Z / south). `engine/heading.ts` converts between them and is
 * self-inverse. Getting this wrong shows up as aircraft sliding tail-first.
 */
import { EASES, samplePolyline, smoothPath, type Pt } from '@/demo/masters-planner/motion/easing'
import { compassToSceneRotation } from '@/demo/masters-planner/engine/heading'
import type { CinematicPath } from '../types'

export interface ActorPose {
  /** Scene feet [x, y, z] — y carries altitude. */
  position: [number, number, number]
  /** Scene Y-rotation degrees, ready for `setAircraftTransform`. */
  rotationDeg: number
}

/** Catmull-Rom samples per corner when `smooth` is on. */
const SMOOTH_SAMPLES = 8

/**
 * Cache of smoothed polylines. Smoothing is pure and the input rarely changes,
 * but this runs once per actor per frame — recomputing an 8x densified spline
 * every frame for every actor would dominate the frame budget.
 *
 * Keyed by the path object identity: the store is immutable, so any edit
 * produces a new object and naturally invalidates the entry.
 */
const smoothCache = new WeakMap<CinematicPath, Pt[]>()

function resolvedPoints(path: CinematicPath): Pt[] {
  const cached = smoothCache.get(path)
  if (cached) return cached
  const raw: Pt[] = path.waypoints.map((w) => [w.pos[0], w.pos[1]])
  const pts = path.smooth && raw.length >= 3 ? smoothPath(raw, SMOOTH_SAMPLES) : raw
  smoothCache.set(path, pts)
  return pts
}

/** Altitude at progress `e`, interpolated across the authored waypoints. */
function altitudeAt(path: CinematicPath, e: number): number {
  const wps = path.waypoints
  if (wps.length === 0) return 0
  if (wps.length === 1) return wps[0].altitudeFt
  const scaled = e * (wps.length - 1)
  const i = Math.min(wps.length - 2, Math.floor(scaled))
  const local = scaled - i
  return wps[i].altitudeFt + (wps[i + 1].altitudeFt - wps[i].altitudeFt) * local
}

/**
 * Fold elapsed time into a 0..1 traversal progress according to `playMode`.
 * Returns null when the actor is not yet moving or has finished a `once` run
 * (callers then hold the resting/final pose).
 */
function progressAt(path: CinematicPath, tMs: number): number | null {
  const local = tMs - path.startOffsetMs
  if (local < 0) return null
  if (path.durationMs <= 0) return 1

  const laps = local / path.durationMs

  switch (path.playMode) {
    case 'once':
      return laps >= 1 ? 1 : laps
    case 'loop':
      return laps % 1
    case 'pingPong': {
      const cycle = laps % 2
      return cycle <= 1 ? cycle : 2 - cycle
    }
  }
}

/**
 * Pose of a path-driven actor at scene time `tMs`.
 *
 * `restHeadingDeg` (compass) is used before the path starts and whenever
 * `headingMode` is 'fixed' with no explicit override.
 */
export function sampleActorPose(
  path: CinematicPath,
  tMs: number,
  restPosition: [number, number],
  restHeadingDeg: number,
  restAltitudeFt: number
): ActorPose {
  const u = progressAt(path, tMs)

  // Before the start offset: hold the resting pose.
  if (u == null) {
    return {
      position: [restPosition[0], restAltitudeFt, restPosition[1]],
      rotationDeg: compassToSceneRotation(restHeadingDeg),
    }
  }

  const pts = resolvedPoints(path)
  if (pts.length < 2) {
    return {
      position: [restPosition[0], restAltitudeFt, restPosition[1]],
      rotationDeg: compassToSceneRotation(restHeadingDeg),
    }
  }

  const ease = EASES[path.easing] ?? EASES.inOut
  // Ping-pong must not ease each half independently — that would stall at the
  // midpoint. `u` is already the folded position, so ease it directly.
  const e = ease(u)

  const { position, tangentDeg } = samplePolyline(pts, e)

  // Autorotation: the tangent IS the compass heading of travel.
  let headingDeg: number
  if (path.headingMode === 'tangent') {
    headingDeg = tangentDeg
    // Travelling backwards on the return leg must turn the nose around, not
    // reverse the model down its own path.
    if (path.playMode === 'pingPong') {
      const laps = (tMs - path.startOffsetMs) / path.durationMs
      const returning = laps % 2 > 1
      if (returning) headingDeg = tangentDeg + 180
    }
  } else {
    headingDeg = path.fixedHeadingDeg ?? restHeadingDeg
  }

  return {
    position: [position[0], altitudeAt(path, e), position[1]],
    rotationDeg: compassToSceneRotation(headingDeg),
  }
}

/** Resting pose for an actor with no path. */
export function restPose(
  position: [number, number],
  headingDeg: number,
  altitudeFt: number
): ActorPose {
  return {
    position: [position[0], altitudeFt, position[1]],
    rotationDeg: compassToSceneRotation(headingDeg),
  }
}
